import apiClient from './client';

export interface ReviewRecordFlag {
  id: string;
  flag_definition_id: string;
  fact_summary: string;
  source_citation: string;
  points: number;
  severity: string;
  override_status?: string | null;
  override_reason?: string | null;
  overridden_at?: string | null;
}

export interface ReviewRecordRun {
  id: string;
  run_number: number;
  status: string;
  score: { aggregate_score: number; tier: string; has_auto_escalate: boolean } | null;
  flags: ReviewRecordFlag[];
  summary: { summary_text: string; model_used: string | null; generated_at: string | null } | null;
  started_at: string | null;
  completed_at: string | null;
}

export interface ReviewRecord {
  case: Record<string, unknown>;
  facts: Array<Record<string, unknown>>;
  documents: Array<Record<string, unknown>>;
  public_data: Array<Record<string, unknown>>;
  review_runs: ReviewRecordRun[];
  determination: Record<string, unknown> | null;
  workflow_steps: Array<Record<string, unknown>>;
  checklist_items: Array<Record<string, unknown>>;
  notes: Array<Record<string, unknown>>;
  audit_trail: Array<Record<string, unknown>>;
  generated_at: string;
}

export async function getReviewRecord(caseId: string): Promise<ReviewRecord> {
  const { data } = await apiClient.get<ReviewRecord>(`/cases/${caseId}/review-record`);
  return data;
}

/** Fetch the print-optimized HTML and open it in a new tab for print-to-PDF. */
export async function openReviewRecordHtml(caseId: string): Promise<void> {
  const resp = await apiClient.get<string>(`/cases/${caseId}/review-record.html`, {
    responseType: 'text',
    transformResponse: (d) => d, // keep raw HTML
  });
  const blob = new Blob([resp.data as unknown as string], { type: 'text/html' });
  const url = URL.createObjectURL(blob);
  const w = window.open(url, '_blank');
  if (!w) {
    window.location.href = url;
  }
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

/** Trigger a browser download of the review record as JSON. */
export async function downloadReviewRecordJson(caseId: string): Promise<void> {
  const record = await getReviewRecord(caseId);
  const blob = new Blob([JSON.stringify(record, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `review-record-${record.case.case_number || caseId}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 10000);
}
